import { useState, useEffect } from 'react';
import {
  DashboardResponse,
  LegacyBallDashboardData,
} from '../../../types/dashboard';
import { StateIcon } from '../StateIcon';
import { WorldMapSvg } from '../WorldMapSvg';

interface ReunionWeekendPanelProps {
  data: DashboardResponse;
}

/** Hardcoded chapter timeline data matching the mockup */
const CHAPTERS_TIMELINE = [
  { year: 2010, name: 'Georgia Chapter', label: 'Joined KDU Global' },
  { year: 2012, name: 'California Chapter', label: 'Joined KDU Global' },
  { year: 2014, name: 'Maryland Chapter', label: 'Joined KDU Global' },
  { year: 2016, name: 'New York Chapter', label: 'Joined KDU Global' },
  { year: 2018, name: 'Texas Chapter', label: 'Joined KDU Global' },
  { year: 2022, name: 'UK Chapter', label: 'Joined KDU Global' },
  { year: 2026, name: 'Atlanta Reunion', label: 'Global Reunion Weekend' },
];

const FALLBACK_FUN_FACTS = [
  'The Krio people are descendants of freed African slaves who settled in Freetown, Sierra Leone.',
  'KDU stands for unity across generations — connecting family from Freetown to the world.',
  'Georgia is hosting its first KDU Global Reunion with chapters from 4 continents!',
];

/**
 * Reunion Weekend dashboard panel — a reimagined display for the
 * "reunion-weekend" area slug with a premium dark-green + gold aesthetic.
 */
export function ReunionWeekendPanel({ data }: ReunionWeekendPanelProps) {
  const ballData = data as LegacyBallDashboardData;
  const [funFactIndex, setFunFactIndex] = useState(0);

  // Build fun facts list from API data or fallback
  const funFacts =
    data.funContent && data.funContent.length > 0
      ? data.funContent.map((f) => f.text)
      : FALLBACK_FUN_FACTS;

  // Auto-rotate fun facts every 8 seconds
  useEffect(() => {
    if (funFacts.length <= 1) return;
    const interval = setInterval(() => {
      setFunFactIndex((prev) => (prev + 1) % funFacts.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [funFacts.length]);

  // Use chaptersTimeline from API if available, otherwise use hardcoded
  const timeline =
    data.chaptersTimeline && data.chaptersTimeline.length > 0
      ? data.chaptersTimeline.map((ch) => ({
          year: ch.inceptionYear,
          name: ch.name,
          label: 'Joined KDU Global',
        }))
      : CHAPTERS_TIMELINE;

  // Totals for chapter summary
  const totalReg = ballData.registrationsByChapter?.reduce(
    (sum, c) => sum + c.registrationCount,
    0,
  ) ?? 0;
  const totalIn = ballData.registrationsByChapter?.reduce(
    (sum, c) => sum + c.checkedInCount,
    0,
  ) ?? 0;

  // SVG progress ring
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const progress = ballData.checkInRate ?? 0;
  const dashOffset = circumference - (progress / 100) * circumference;

  const now = new Date();
  const lastUpdated = now.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const lastTime = now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  return (
    <div style={styles.wrapper}>
      {/* ===== HEADER ===== */}
      <header style={{ ...styles.header, position: 'relative' }}>
        {/* Background image placeholder */}
        <img
          src="/images/states/header-bg.png"
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.3, zIndex: 0, borderRadius: 12 }}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
        <div style={{ ...styles.headerLeft, zIndex: 1 }}>
          <img
            src="/images/states/kdu-seal.png"
            alt="KDU Global"
            style={{ width: 90, height: 90, objectFit: 'contain', borderRadius: '50%', backgroundColor: '#E39FF6', padding: 6 }}
            onError={(e) => { (e.target as HTMLImageElement).src = '/KDULogo.PNG'; }}
          />
        </div>
        <div style={{ ...styles.headerCenter, zIndex: 1 }}>
          <h1 style={styles.headerTitle}>EDU REUNION WEEKEND</h1>
          <p style={styles.headerSubtitle}>
            <span style={{ color: '#d4af37' }}>• · •</span>{' '}2026{' '}<span style={{ color: '#1a6b3c' }}>• · •</span>
          </p>
          <p style={{ ...styles.headerSubtitle, marginTop: 4 }}>
            Sabi you Kolcho
          </p>
        </div>
        <div style={{ ...styles.headerRight, zIndex: 1 }}>
          <span style={styles.headerSmallGold}>📅 SEPTEMBER 3 – 7, 2026</span>
          <span style={styles.headerSmallGold}>📍 Atlanta, Georgia, USA</span>
        </div>
      </header>

      {/* ===== STATS ROW ===== */}
      <div style={styles.statsRow}>
        {/* Stat 1: Total Registered */}
        <div style={styles.statCard}>
          <span style={styles.statLabel}>TOTAL REGISTERED</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 48, height: 48, borderRadius: '50%', border: '2px solid #1b2557', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>👥</span>
            <span style={styles.statNumber}>{ballData.totalRegistrations?.toLocaleString()}</span>
          </div>
          <span style={styles.statSub}>AND COUNTING!</span>
        </div>

        {/* Stat 2: Total Checked In */}
        <div style={styles.statCard}>
          <span style={styles.statLabel}>TOTAL CHECKED IN</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 48, height: 48, borderRadius: '50%', border: '2px solid #1b2557', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>📋</span>
            <span style={styles.statNumber}>{ballData.totalCheckedIn?.toLocaleString()}</span>
          </div>
          <span style={styles.statSub}>WELCOME HOME!</span>
        </div>

        {/* Stat 3: % Check-In with SVG ring */}
        <div style={{ ...styles.statCard, flexDirection: 'row', alignItems: 'center', gap: 20 }}>
          <div style={{ position: 'relative', width: 130, height: 130, flexShrink: 0 }}>
            <svg width="130" height="130" viewBox="0 0 130 130">
              <circle
                cx="65"
                cy="65"
                r={radius}
                fill="none"
                stroke="rgba(26, 107, 60, 0.3)"
                strokeWidth="16"
              />
              <circle
                cx="65"
                cy="65"
                r={radius}
                fill="none"
                stroke="#1b2557"
                strokeWidth="16"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                transform="rotate(-90 65 65)"
                style={{ transition: 'stroke-dashoffset 1s ease-in-out' }}
              />
            </svg>
            <div style={styles.ringCenter}>
              <span style={{ fontSize: 'clamp(18px, 2.5vw, 28px)', fontWeight: 900, color: '#1b2557' }}>
                {progress}%
              </span>
              <span style={{ fontSize: 10, color: '#64748b' }}>CHECK-IN</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={styles.statLabel}>% CHECK-IN</span>
            <span style={{ fontSize: 13, color: '#64748b' }}>GOAL 1,500 REGISTERED</span>
          </div>
        </div>

        {/* Stat 4: Fun Facts */}
        <div style={styles.statCard}>
          <span style={styles.statLabel}>FUN FACTS – DID YOU KNOW?</span>
          <p style={styles.funFactText}>❝ {funFacts[funFactIndex]}</p>
          <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
            {funFacts.map((_, i) => (
              <span key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: i === funFactIndex ? '#1b2557' : '#ccc' }} />
            ))}
          </div>
        </div>
      </div>

      {/* ===== BOTTOM SECTION (3 columns) ===== */}
      <div style={styles.bottomSection}>
        {/* Column 1: Where Our Guests Are From */}
        <div style={{ ...styles.column, position: 'relative', overflow: 'hidden', flexDirection: 'row', gap: 16 }}>
          {/* Left: chapter list */}
          <div style={{ flex: '0 0 35%', display: 'flex', flexDirection: 'column', zIndex: 1 }}>
            <h2 style={styles.colTitle}>WHERE OUR GUESTS ARE FROM</h2>
            <p style={styles.colSubtitle}>REGISTERED / CHECKED-IN</p>
          <div style={styles.chapterList}>
            {ballData.registrationsByChapter?.map((item) => (
              <div key={item.chapter} style={styles.chapterRow}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <StateIcon state={item.chapter} size={28} />
                  <span style={{ color: '#ffffff', fontSize: 'clamp(12px, 1.4vw, 16px)', textTransform: 'uppercase' }}>
                    {item.chapter}
                  </span>
                </div>
                <span style={{ color: '#1b2557', fontWeight: 900, fontSize: 'clamp(12px, 1.4vw, 16px)' }}>
                  {item.registrationCount}&nbsp;&nbsp;/&nbsp;&nbsp;{item.checkedInCount}
                </span>
              </div>
            ))}
            {/* Total row */}
            <div style={{ ...styles.chapterRow, borderTop: '1px solid #1a6b3c', marginTop: 8, paddingTop: 8 }}>
              <span style={{ color: '#1b2557', fontWeight: 900, fontSize: 'clamp(13px, 1.5vw, 17px)' }}>
                TOTAL
              </span>
              <span style={{ color: '#1b2557', fontWeight: 900, fontSize: 'clamp(13px, 1.5vw, 17px)' }}>
                {totalReg}&nbsp;&nbsp;/&nbsp;&nbsp;{totalIn}
              </span>
            </div>
          </div>
          <p style={{ display: 'none' }}>placeholder</p>
          </div>
          {/* Right: world map placeholder */}
          <div style={{ flex: '1 1 60%', position: 'relative', minHeight: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <img
              src="/images/states/world-map.png"
              alt="World map with connection lines"
              style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8, flex: 1 }}
              onError={(e) => {
                // Show placeholder if image not found
                const el = e.target as HTMLImageElement;
                el.style.display = 'none';
                el.parentElement!.style.border = '2px dashed #1a6b3c';
                el.parentElement!.innerHTML = '<span style="color:#64748b;font-size:14px;text-align:center">🌍 World Map<br/>(drop world-map.png in /images/states/)</span>';
              }}
            />
            <div style={{ background: 'rgba(27, 37, 87, 0.1)', border: '3px solid #1a6b3c', borderRadius: 20, padding: '8px 20px', textAlign: 'center' }}>
              <span style={{ color: '#1b2557', fontSize: 'clamp(12px, 1.4vw, 16px)', fontWeight: 600, letterSpacing: 1 }}>🌐 KRIOS ACROSS THE GLOBE, ONE FAMILY, ONE LEGACY.</span>
            </div>
          </div>
        </div>

        {/* Column 2: Who's Checked In? By Role */}
        <div style={styles.column}>
          <h2 style={styles.colTitle}>WHO'S CHECKED IN? BY ROLE</h2>
          <div style={styles.roleGrid}>
            {ballData.registrationsByRole?.map((item) => {
              const pct = ballData.totalRegistrations > 0
                ? Math.round((item.count / ballData.totalRegistrations) * 100)
                : 0;
              const roleIcon = item.role.toLowerCase() === 'attendee' ? '👤' : (item.role.toLowerCase() === 'organizer' || item.role.toLowerCase() === 'admin' ? '⭐' : '👤');
              return (
                <div key={item.role} style={styles.roleCard}>
                  <div style={styles.roleBadge}>
                    <span style={{ fontSize: 'clamp(14px, 1.5vw, 18px)' }}>{roleIcon}</span>
                  </div>
                  <span style={{ fontSize: 'clamp(22px, 3vw, 36px)', fontWeight: 900, color: '#1b2557' }}>
                    {item.count}
                  </span>
                  <span style={{ fontSize: 'clamp(11px, 1.2vw, 14px)', color: '#64748b', textTransform: 'uppercase' }}>
                    ({pct}%)
                  </span>
                  <span style={{ fontSize: 'clamp(11px, 1.2vw, 14px)', color: '#64748b', textTransform: 'uppercase' }}>
                    {item.role}
                  </span>
                </div>
              );
            })}
          </div>
          <p style={styles.thankYou}>
            Thank You TO OUR LEADERS AND VOLUNTEERS!
          </p>
        </div>

        {/* Column 3: Our Journey Together */}
        <div style={styles.column}>
          <h2 style={styles.colTitle}>OUR JOURNEY TOGETHER</h2>
          <p style={styles.colSubtitle}>CHAPTERS JOINING THE GLOBAL BODY</p>
          <div style={styles.timeline}>
            {timeline.map((item, idx) => (
              <div key={idx} style={styles.timelineItem}>
                <div style={styles.timelineDot} />
                {idx < timeline.length - 1 && <div style={styles.timelineLine} />}
                <div style={{ marginLeft: 16 }}>
                  <span style={{ color: '#1b2557', fontWeight: 900, fontSize: 'clamp(13px, 1.5vw, 16px)' }}>
                    {item.year}
                  </span>
                  <span style={{ color: '#1b2557', marginLeft: 10, fontSize: 'clamp(12px, 1.3vw, 15px)' }}>
                    {item.name}
                  </span>
                  <span style={{ color: '#64748b', marginLeft: 8, fontSize: 'clamp(10px, 1vw, 12px)' }}>
                    – {item.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== FOOTER ===== */}
      <footer style={styles.footer}>
        <p style={styles.footerQuote}>
          "FROM FREETOWN TO THE WORLD, OUR STORY CONTINUES."
        </p>
        <div style={styles.footerRow}>
          <span style={styles.footerHashtags}>
            #KDUGlobal2026 #KrioPride #OneFamily #OneLegacy
          </span>
          <span style={styles.footerTimestamp}>
            🔄 LAST UPDATED: {lastUpdated} • {lastTime}
          </span>
          <span style={styles.footerScript}>
            Heritage Unity Legacy Future
          </span>
        </div>
      </footer>
    </div>
  );
}

/* ─── Inline Styles ──────────────────────────────────────────── */

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    overflow: 'auto',
    background: 'linear-gradient(180deg, #E39FF6 0%, #E39FF6 100%)',
    color: '#1b2557',
    fontFamily: "'Inter', system-ui, sans-serif",
    display: 'flex',
    flexDirection: 'column',
    padding: 'clamp(16px, 3vw, 40px)',
    gap: 'clamp(12px, 2vw, 24px)',
  },

  /* Header */
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
  },
  headerCenter: {
    textAlign: 'center',
    flex: 1,
    minWidth: 200,
  },
  headerRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 2,
  },
  headerSmallGold: {
    color: '#1b2557',
    fontSize: 'clamp(11px, 1.3vw, 15px)',
    fontWeight: 600,
    letterSpacing: 1,
  },
  headerTitle: {
    color: '#1b2557',
    fontSize: 'clamp(22px, 4vw, 48px)',
    fontWeight: 700,
    margin: 0,
    fontFamily: "'Georgia', serif",
    letterSpacing: 2,
  },
  headerSubtitle: {
    color: '#1b2557',
    fontSize: 'clamp(10px, 1.2vw, 14px)',
    margin: '6px 0 0 0',
    letterSpacing: 2,
    opacity: 0.85,
  },

  /* Stats Row */
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 'clamp(10px, 1.5vw, 20px)',
  },
  statCard: {
    background: 'transparent',
    border: '3px solid #1a6b3c',
    borderRadius: 12,
    padding: 'clamp(14px, 2vw, 24px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    textAlign: 'center',
    backdropFilter: 'blur(8px)',
  },
  statLabel: {
    color: '#ffffff',
    fontSize: 'clamp(13px, 1.6vw, 18px)',
    fontWeight: 700,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
  },
  statNumber: {
    color: '#1b2557',
    fontSize: 'clamp(48px, 7vw, 80px)',
    fontWeight: 900,
    lineHeight: 1.1,
  },
  statSub: {
    color: '#1a6b3c',
    fontSize: 'clamp(12px, 1.4vw, 16px)',
    fontWeight: 700,
    letterSpacing: 1,
  },
  ringCenter: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  funFactText: {
    color: '#1b2557',
    fontSize: 'clamp(12px, 1.3vw, 16px)',
    margin: 0,
    lineHeight: 1.5,
    fontStyle: 'italic',
    opacity: 0.9,
  },

  /* Bottom Section */
  bottomSection: {
    display: 'grid',
    gridTemplateColumns: '2.5fr 0.8fr 1.5fr',
    gap: 'clamp(12px, 2vw, 28px)',
    flex: 1,
    overflow: 'hidden',
  },
  column: {
    background: 'transparent',
    border: '3px solid #1a6b3c',
    borderRadius: 12,
    padding: 'clamp(14px, 2vw, 24px)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    backdropFilter: 'blur(8px)',
  },
  colTitle: {
    color: '#ffffff',
    fontSize: 'clamp(16px, 2.2vw, 24px)',
    fontWeight: 700,
    margin: '0 0 4px 0',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  colSubtitle: {
    color: '#64748b',
    fontSize: 'clamp(10px, 1vw, 12px)',
    margin: '0 0 12px 0',
    letterSpacing: 1,
  },

  /* Chapter List */
  chapterList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    flex: 1,
  },
  chapterRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 0',
    borderBottom: '1px solid rgba(26, 107, 60, 0.3)',
  },
  tagline: {
    color: '#1b2557',
    fontSize: 'clamp(11px, 1.3vw, 14px)',
    textAlign: 'center',
    marginTop: 12,
    fontStyle: 'italic',
    fontWeight: 600,
    letterSpacing: 1,
  },

  /* Role Grid */
  roleGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'clamp(14px, 2vw, 24px)',
    justifyContent: 'center',
    flex: 1,
    alignItems: 'center',
  },
  roleCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    minWidth: 80,
  },
  roleBadge: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: '#1b2557',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thankYou: {
    color: '#1b2557',
    fontSize: 'clamp(11px, 1.2vw, 14px)',
    textAlign: 'center',
    fontFamily: "'Georgia', serif",
    fontStyle: 'italic',
    marginTop: 'auto',
    paddingTop: 12,
  },

  /* Timeline */
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    flex: 1,
  },
  timelineItem: {
    display: 'flex',
    alignItems: 'flex-start',
    position: 'relative',
    paddingLeft: 18,
    paddingBottom: 16,
  },
  timelineDot: {
    position: 'absolute',
    left: 0,
    top: 4,
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: '#1b2557',
    border: '2px solid #E39FF6',
  },
  timelineLine: {
    position: 'absolute',
    left: 5,
    top: 16,
    width: 2,
    height: 'calc(100% - 12px)',
    background: '#1a6b3c',
  },

  /* Footer */
  footer: {
    textAlign: 'center',
    paddingTop: 8,
    borderTop: '1px solid #1a6b3c',
  },
  footerQuote: {
    color: '#1b2557',
    fontStyle: 'italic',
    fontSize: 'clamp(12px, 1.4vw, 16px)',
    margin: '0 0 8px 0',
    fontFamily: "'Georgia', serif",
  },
  footerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  footerHashtags: {
    color: '#1b2557',
    fontSize: 'clamp(9px, 1vw, 12px)',
    letterSpacing: 0.5,
  },
  footerTimestamp: {
    color: '#64748b',
    fontSize: 'clamp(9px, 1vw, 11px)',
  },
  footerScript: {
    color: '#1b2557',
    fontStyle: 'italic',
    fontSize: 'clamp(10px, 1.1vw, 13px)',
    fontFamily: "'Brush Script MT', 'Georgia', cursive, serif",
  },
};
